import React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUserPermissions } from '@/store/useUserPermissions';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Plus, Save, XCircle } from 'lucide-react';

const GET_PROGRAMS = gql`
  query AdminNotificationPrograms {
    adminNotificationPrograms {
      id
      name
      slug
    }
  }
`;

const GET_GROUPS = gql`
  query AdminNotificationGroups {
    adminNotificationGroups {
      id
      title
      subTitle
    }
  }
`;

const GET_TEMPLATES = gql`
  query AdminNotificationTemplates($programID: Int!) {
    adminNotificationTemplates(programID: $programID) {
      id
      programID
      name
      emailID
      groupId
      group
      subTitle
      toAddress
      ccAddress
      replyToAddress
      subject
      body
      variables
      active
      includeProvidersEmail
      includeLocationsEmail
      position
    }
  }
`;

const GET_VARIABLES = gql`
  query AdminNotificationVariables($programID: Int!) {
    adminNotificationVariables(programID: $programID)
  }
`;

const SAVE_TEMPLATE = gql`
  mutation AdminSaveNotificationTemplate(
    $id: Int
    $programID: Int!
    $title: String!
    $emailID: String!
    $groupId: Int
    $toAddress: String
    $ccAddress: String
    $replyToAddress: String
    $subject: String
    $body: String
    $variables: [String]
    $includeProvidersEmail: Boolean
    $includeLocationsEmail: Boolean
    $active: Boolean
    $position: Int
  ) {
    adminSaveNotificationTemplate(
      id: $id
      programID: $programID
      title: $title
      emailID: $emailID
      groupId: $groupId
      toAddress: $toAddress
      ccAddress: $ccAddress
      replyToAddress: $replyToAddress
      subject: $subject
      body: $body
      variables: $variables
      includeProvidersEmail: $includeProvidersEmail
      includeLocationsEmail: $includeLocationsEmail
      active: $active
      position: $position
    ) {
      message
      responseCode
      responseID
    }
  }
`;

const TOGGLE_TEMPLATE = gql`
  mutation AdminDeactivateNotificationTemplate($id: Int!, $active: Boolean!) {
    adminDeactivateNotificationTemplate(id: $id, active: $active) {
      message
      responseCode
      responseID
    }
  }
`;

type Program = {
  id: number;
  name: string;
  slug: string;
};

type Group = {
  id: number;
  title: string;
  subTitle?: string | null;
};

type NotificationTemplate = {
  id: number;
  programID: number;
  name: string;
  emailID: string;
  groupId?: number | null;
  group?: string | null;
  subTitle?: string | null;
  toAddress?: string | null;
  ccAddress?: string | null;
  replyToAddress?: string | null;
  subject?: string | null;
  body?: string | null;
  variables?: string[] | null;
  active?: boolean | null;
  includeProvidersEmail?: boolean | null;
  includeLocationsEmail?: boolean | null;
  position?: number | null;
};

type FormState = {
  id?: number | null;
  programID: number | null;
  name: string;
  emailID: string;
  groupId: number | null;
  toAddress: string;
  ccAddress: string;
  replyToAddress: string;
  subject: string;
  body: string;
  variables: string;
  includeProvidersEmail: boolean;
  includeLocationsEmail: boolean;
  active: boolean;
  position: number | null;
};

type SaveNotice = {
  type: 'success' | 'error';
  message: string;
};

const emptyForm = (programID: number | null): FormState => ({
  id: null,
  programID,
  name: '',
  emailID: '',
  groupId: null,
  toAddress: '',
  ccAddress: '',
  replyToAddress: '',
  subject: '',
  body: '',
  variables: '',
  includeProvidersEmail: false,
  includeLocationsEmail: false,
  active: true,
  position: null,
});

const buildFormState = (template: NotificationTemplate): FormState => ({
  id: template.id,
  programID: template.programID,
  name: template.name || '',
  emailID: template.emailID || '',
  groupId: template.groupId ?? null,
  toAddress: template.toAddress || '',
  ccAddress: template.ccAddress || '',
  replyToAddress: template.replyToAddress || '',
  subject: template.subject || '',
  body: template.body || '',
  variables: (template.variables || []).join(', '),
  includeProvidersEmail: Boolean(template.includeProvidersEmail),
  includeLocationsEmail: Boolean(template.includeLocationsEmail),
  active: template.active !== false,
  position: template.position ?? null,
});

const normalizeVariables = (value: string): string[] => {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const hasPolicy = (policies: string[], required: string) => {
  const match = (userPolicy: string, req: string) => {
    if (!userPolicy || !req) return false;
    const [uRes, uAct = ''] = userPolicy.split('~');
    const [rRes, rAct = ''] = req.split('~');
    if (!uRes || !rRes) return false;

    const resMatch = (() => {
      const u = uRes.split(':');
      const r = rRes.split(':');
      const len = Math.max(u.length, r.length);
      for (let i = 0; i < len; i += 1) {
        const up = u[i] || '*';
        const rp = r[i] || '*';
        if (up === '*' || rp === '*') continue;
        if (up !== rp) return false;
      }
      return true;
    })();

    const actMatch = (() => {
      if (!rAct || !uAct) return rAct === uAct;
      if (rAct === '*' || uAct === '*') return true;
      return rAct === uAct;
    })();

    return resMatch && actMatch;
  };

  return policies.some((p) => p === '*:*~*' || match(p, required));
};

export const NotificationSettings: React.FC = () => {
  const { policies, isSecurityLoading } = useUserPermissions();
  const canRead = hasPolicy(policies, 'admin:access~full');
  const canWrite = hasPolicy(policies, 'admin:access~full');

  const { data: programsData, loading: programsLoading } = useQuery(GET_PROGRAMS, {
    skip: !canRead
  });
  const { data: groupsData } = useQuery(GET_GROUPS, {
    skip: !canRead
  });

  const [selectedProgramId, setSelectedProgramId] = React.useState<number | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<number | null>(null);
  const [formState, setFormState] = React.useState<FormState>(emptyForm(null));
  const [saveNotice, setSaveNotice] = React.useState<SaveNotice | null>(null);

  const { data: templatesData, loading: templatesLoading, refetch: refetchTemplates } = useQuery(
    GET_TEMPLATES,
    {
      variables: { programID: selectedProgramId },
      skip: !canRead || !selectedProgramId
    }
  );

  const { data: variablesData } = useQuery(GET_VARIABLES, {
    variables: { programID: selectedProgramId },
    skip: !canRead || !selectedProgramId
  });

  const [saveTemplate, { loading: saveLoading }] = useMutation(SAVE_TEMPLATE);
  const [toggleTemplate, { loading: toggleLoading }] = useMutation(TOGGLE_TEMPLATE);

  React.useEffect(() => {
    if (selectedProgramId) {
      return;
    }
    const programs = programsData?.adminNotificationPrograms || [];
    if (!programs.length) {
      return;
    }
    setSelectedProgramId(programs[0].id);
  }, [programsData, selectedProgramId]);

  React.useEffect(() => {
    if (!selectedProgramId) {
      return;
    }
    setFormState((prev) => {
      if (prev.programID === selectedProgramId) {
        return prev;
      }
      return emptyForm(selectedProgramId);
    });
    setSelectedTemplateId(null);
    setSaveNotice(null);
  }, [selectedProgramId]);

  React.useEffect(() => {
    if (!templatesData?.adminNotificationTemplates) {
      return;
    }
    if (!selectedTemplateId) {
      return;
    }
    const match = templatesData.adminNotificationTemplates.find(
      (item: NotificationTemplate) => item.id === selectedTemplateId
    );
    if (!match) {
      return;
    }
    setFormState(buildFormState(match));
  }, [templatesData, selectedTemplateId]);

  if (isSecurityLoading) {
    return (
      <div className="p-6">
        <div className="text-sm text-gray-500">Loading permissions...</div>
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>You do not have access to view notifications.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const programs: Program[] = programsData?.adminNotificationPrograms || [];
  const groups: Group[] = groupsData?.adminNotificationGroups || [];
  const templates: NotificationTemplate[] = templatesData?.adminNotificationTemplates || [];
  const allowedVariables: string[] = variablesData?.adminNotificationVariables || [];

  const handleSelectTemplate = (template: NotificationTemplate) => {
    setSelectedTemplateId(template.id);
    setFormState(buildFormState(template));
    setSaveNotice(null);
  };

  const handleCreateNew = () => {
    setSelectedTemplateId(null);
    setFormState(emptyForm(selectedProgramId));
    setSaveNotice(null);
  };

  const handleSave = async () => {
    if (!canWrite) {
      return;
    }
    if (!formState.programID) {
      return;
    }
    if (!formState.name.trim()) {
      return;
    }
    if (!formState.emailID.trim()) {
      return;
    }

    setSaveNotice(null);

    try {
      const result = await saveTemplate({
        variables: {
          id: formState.id || undefined,
          programID: formState.programID,
          title: formState.name.trim(),
          emailID: formState.emailID.trim(),
          groupId: formState.groupId || undefined,
          toAddress: formState.toAddress,
          ccAddress: formState.ccAddress,
          replyToAddress: formState.replyToAddress,
          subject: formState.subject,
          body: formState.body,
          variables: normalizeVariables(formState.variables),
          includeProvidersEmail: formState.includeProvidersEmail,
          includeLocationsEmail: formState.includeLocationsEmail,
          active: formState.active,
          position: formState.position || undefined
        }
      });

      const payload = result.data?.adminSaveNotificationTemplate;
      if (!payload || Number(payload.responseCode) !== 200) {
        setSaveNotice({
          type: 'error',
          message: payload?.message || 'Unable to save notification template.'
        });
        return;
      }

      const savedId = Number(payload.responseID);
      const refetchResult = await refetchTemplates();
      const refreshedTemplates: NotificationTemplate[] = refetchResult.data?.adminNotificationTemplates || [];
      const savedTemplate = refreshedTemplates.find((template) => template.id === savedId)
        || refreshedTemplates.find((template) => template.emailID === formState.emailID.trim());

      if (savedTemplate) {
        setSelectedTemplateId(savedTemplate.id);
        setFormState(buildFormState(savedTemplate));
      } else if (Number.isFinite(savedId) && savedId !== 0) {
        setSelectedTemplateId(savedId);
        setFormState((prev) => ({ ...prev, id: savedId }));
      }

      setSaveNotice({ type: 'success', message: payload.message || 'Template saved.' });
    } catch (error) {
      setSaveNotice({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to save notification template.'
      });
    }
  };

  const handleToggleActive = async () => {
    if (!canWrite) {
      return;
    }
    if (!formState.id) {
      return;
    }
    const nextActive = !formState.active;
    setSaveNotice(null);

    try {
      const result = await toggleTemplate({
        variables: {
          id: formState.id,
          active: nextActive
        }
      });
      const payload = result.data?.adminDeactivateNotificationTemplate;
      if (!payload || Number(payload.responseCode) !== 200) {
        setSaveNotice({
          type: 'error',
          message: payload?.message || 'Unable to update notification template.'
        });
        return;
      }
      setFormState((prev) => ({ ...prev, active: nextActive }));
      await refetchTemplates();
      setSaveNotice({ type: 'success', message: payload.message || 'Template updated.' });
    } catch (error) {
      setSaveNotice({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to update notification template.'
      });
    }
  };

  return (
    <div className="p-6 space-y-6 min-h-screen flex flex-col">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Notification Templates</h1>
          <p className="text-sm text-gray-500">Manage email recipients and templates.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={selectedProgramId ? String(selectedProgramId) : undefined}
            onValueChange={(value) => setSelectedProgramId(Number(value))}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select program" />
            </SelectTrigger>
            <SelectContent>
              {programs.map((program) => (
                <SelectItem key={program.id} value={String(program.id)}>
                  {program.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canWrite && (
            <Button variant="outline" onClick={handleCreateNew} className="flex items-center gap-2">
              <Plus className="h-4 w-4" /> New Template
            </Button>
          )}
        </div>
      </div>

      {saveNotice ? (
        <Alert
          variant={saveNotice.type === 'error' ? 'destructive' : 'default'}
          className={saveNotice.type === 'success' ? 'border-green-200 bg-green-50 text-green-900' : undefined}
        >
          {saveNotice.type === 'success' ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{saveNotice.message}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-6 flex-1 min-h-0">
        <Card className="border border-gray-200 max-h-[100vh]">
          <CardContent className="p-0 max-h-[100vh] flex flex-col">
            <div className="border-b px-4 py-3 text-sm font-semibold text-gray-700">Templates</div>
            <div className="flex-1 overflow-y-auto">
              {programsLoading || templatesLoading ? (
                <div className="p-4 text-sm text-gray-500">Loading templates...</div>
              ) : null}
              {!templatesLoading && templates.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No templates found.</div>
              ) : null}
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className={
                    selectedTemplateId === template.id
                      ? 'w-full text-left px-4 py-3 border-b bg-blue-50'
                      : 'w-full text-left px-4 py-3 border-b hover:bg-gray-50'
                  }
                >
                  <div className="text-sm font-medium text-gray-900">{template.name}</div>
                  <div className="text-xs text-gray-500">{template.emailID}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 max-h-[100vh]">
          <CardContent className="p-6 space-y-4 max-h-[100vh] overflow-y-auto">
            {allowedVariables.length > 0 ? (
              <div className="text-sm font-semibold text-gray-700">
                Allowed variables: {allowedVariables.join(', ')}
              </div>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-3">
                <label className="text-sm font-medium text-gray-700">Template Name</label>
                <Input
                  value={formState.name}
                  onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Template name"
                  disabled={!canWrite}
                />
              </div>
              <div className="md:col-span-3">
                <label className="text-sm font-medium text-gray-700">Email ID</label>
                <Input
                  value={formState.emailID}
                  onChange={(event) => setFormState((prev) => ({ ...prev, emailID: event.target.value }))}
                  placeholder="email_id"
                  disabled={!canWrite}
                />
              </div>
              <div className="md:col-span-5">
                <label className="text-sm font-medium text-gray-700">Notification Group</label>
                <Select
                  value={formState.groupId ? String(formState.groupId) : 'none'}
                  onValueChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      groupId: value === 'none' ? null : Number(value)
                    }))
                  }
                  disabled={!canWrite}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {groups.map((group) => (
                      <SelectItem key={group.id} value={String(group.id)}>
                        {group.title}
                        {group.subTitle ? ` - ${group.subTitle}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1">
                <label className="text-sm font-medium text-gray-700">Position</label>
                <Select
                  value={formState.position ? String(formState.position) : 'none'}
                  onValueChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      position: value === 'none' ? null : Number(value)
                    }))
                  }
                  disabled={!canWrite}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Order" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {Array.from({ length: 20 }, (_, index) => {
                      const value = index + 1;
                      return (
                        <SelectItem key={value} value={String(value)}>
                          {value}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">To</label>
                <Textarea
                  value={formState.toAddress}
                  onChange={(event) => setFormState((prev) => ({ ...prev, toAddress: event.target.value }))}
                  placeholder="To addresses"
                  rows={3}
                  disabled={!canWrite}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">CC</label>
                <Textarea
                  value={formState.ccAddress}
                  onChange={(event) => setFormState((prev) => ({ ...prev, ccAddress: event.target.value }))}
                  placeholder="CC addresses"
                  rows={3}
                  disabled={!canWrite}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Reply-To</label>
                <Textarea
                  value={formState.replyToAddress}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, replyToAddress: event.target.value }))
                  }
                  placeholder="Reply-to addresses"
                  rows={3}
                  disabled={!canWrite}
                />
              </div>
              <div />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <Input
                value={formState.subject}
                onChange={(event) => setFormState((prev) => ({ ...prev, subject: event.target.value }))}
                placeholder="Subject"
                className="w-full"
                disabled={!canWrite}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Body</label>
              <Textarea
                value={formState.body}
                onChange={(event) => setFormState((prev) => ({ ...prev, body: event.target.value }))}
                placeholder="Body"
                rows={8}
                disabled={!canWrite}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Variables (comma-separated)</label>
              <Input
                value={formState.variables}
                onChange={(event) => setFormState((prev) => ({ ...prev, variables: event.target.value }))}
                placeholder="$firstname, $lastname"
                disabled={!canWrite}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Checkbox
                  checked={formState.includeProvidersEmail}
                  onCheckedChange={(checked) =>
                    setFormState((prev) => ({ ...prev, includeProvidersEmail: Boolean(checked) }))
                  }
                  disabled={!canWrite}
                />
                Include providers
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Checkbox
                  checked={formState.includeLocationsEmail}
                  onCheckedChange={(checked) =>
                    setFormState((prev) => ({ ...prev, includeLocationsEmail: Boolean(checked) }))
                  }
                  disabled={!canWrite}
                />
                Include locations
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Checkbox
                  checked={formState.active}
                  onCheckedChange={(checked) =>
                    setFormState((prev) => ({ ...prev, active: Boolean(checked) }))
                  }
                  disabled={!canWrite}
                />
                Active
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              {canWrite && formState.id && (
                <Button
                  variant="outline"
                  onClick={handleToggleActive}
                  disabled={toggleLoading}
                  className="flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <XCircle className="h-4 w-4" />
                  {formState.active ? 'Deactivate' : 'Activate'}
                </Button>
              )}
              {canWrite && (
                <Button
                  onClick={handleSave}
                  disabled={saveLoading}
                  className="flex items-center gap-2 px-6 shadow-sm"
                >
                  <Save className="h-4 w-4" /> Save Template
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotificationSettings;
